import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, addHours, isBefore } from 'date-fns';
import { pt } from 'date-fns/locale';
import { Calendar as CalendarIcon, Phone, Video, ArrowRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPE_META = {
  evento: { icon: CalendarIcon, color: 'text-sky-600', bg: 'bg-sky-100' },
  reuniao: { icon: Video, color: 'text-violet-600', bg: 'bg-violet-100' },
  chamada: { icon: Phone, color: 'text-emerald-600', bg: 'bg-emerald-100' },
};

export const UpcomingEventsWidget = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);

  const fetchUpcoming = useCallback(async () => {
    try {
      const now = new Date();
      const horizon = addHours(now, 8);
      const from = format(now, "yyyy-MM-dd'T'HH:mm");
      const to = format(horizon, "yyyy-MM-dd'T'HH:mm");
      const r = await axios.get(`${API_URL}/calendar/events`, { params: { from, to } });
      // Keep only user-created events (kind=event) — tickets show elsewhere
      const upcoming = (r.data || [])
        .filter(e => e.kind === 'event' && e.status === 'agendado' && e.datetime)
        .sort((a, b) => (a.datetime || '').localeCompare(b.datetime || ''))
        .slice(0, 3);
      setEvents(upcoming);
    } catch {
      setEvents([]);
    }
  }, []);

  useEffect(() => {
    fetchUpcoming();
    const id = setInterval(fetchUpcoming, 60_000); // refresh every minute
    return () => clearInterval(id);
  }, [fetchUpcoming]);

  if (events.length === 0) return null;

  return (
    <Card className="rounded-2xl border-slate-100" data-testid="upcoming-events-widget">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-[#2D3E6F]" />
            <h3 className="font-semibold text-slate-800 text-sm">Próximos Eventos (8h)</h3>
          </div>
          <button
            type="button"
            onClick={() => navigate('/calendar')}
            className="text-xs text-[#2D3E6F] hover:underline flex items-center gap-1"
            data-testid="upcoming-events-see-all"
          >
            Ver calendário <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        <div className="space-y-2">
          {events.map(ev => {
            const meta = TYPE_META[ev.tipo] || TYPE_META.evento;
            const Icon = meta.icon;
            let when = '';
            let imminent = false;
            try {
              const d = parseISO(ev.datetime);
              when = format(d, "HH:mm", { locale: pt });
              imminent = isBefore(d, addHours(new Date(), 1));
            } catch { /* ignore */ }
            return (
              <button
                key={ev.id}
                type="button"
                onClick={() => navigate(`/calendar?event=${ev.id}`)}
                className={`w-full flex items-center gap-3 p-2.5 rounded-lg border transition-colors text-left ${imminent ? 'border-amber-300 bg-amber-50 hover:bg-amber-100' : 'border-slate-100 hover:bg-slate-50'}`}
                data-testid={`upcoming-event-${ev.id}`}
              >
                <div className={`w-9 h-9 rounded-full ${meta.bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-4 h-4 ${meta.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{ev.title}</p>
                  <p className="text-xs text-slate-500 truncate">
                    {when} · {ev.tipo_label}
                    {ev.contract_name ? ` · ${ev.contract_name}` : ''}
                  </p>
                </div>
                {imminent && (
                  <span className="text-[10px] font-bold bg-amber-500 text-white px-1.5 py-0.5 rounded-full">
                    EM BREVE
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default UpcomingEventsWidget;
